import React, { useState, useEffect } from "react";
import { Constants } from "../utils/constants";
import { ChevronDown, Trash2, MapPin, Calendar } from "lucide-react";
import clsx from "clsx";

const HTable = ({
  data = [],
  columns = [],
  onSelectAll,
  onSelectItem,
  selectedItems = [],
  showSelectAll = true,
  showActions = true,
  className = "",
  onDelete,
  onRowClick,
}) => {
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (selectedItems.length === data.length && data.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, data.length]);

  const handleSelectAll = checked => {
    setSelectAll(checked);
    if (onSelectAll) {
      onSelectAll(checked);
    }
  };

  const handleSelectItem = (item, checked) => {
    if (onSelectItem) {
      onSelectItem(item, checked);
    }
  };

  const isSelected = item => {
    return selectedItems.some(selectedItem => selectedItem === item.id || selectedItem === item);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Desktop Header Row */}
      {showSelectAll && (
        <div className="hidden md:flex bg-transparent rounded-xl px-4 py-3 items-center w-full overflow-x-auto">
          <div className="flex items-center gap-3 w-[120px] min-w-[120px] flex-shrink-0">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={e => handleSelectAll(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 focus:ring-2"
              style={{
                accentColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                "--tw-ring-color": `${Constants.HOST_EXPERIENCE_BASE_COLOR}50`,
              }}
            />
            <span className="text-sm font-medium text-gray-700">
              {columns[0]?.header || "ID"}
              {columns[0]?.sortable && <ChevronDown className="w-3 h-3 text-gray-400 inline ml-1" />}
            </span>
          </div>

          {columns.slice(1).map((column, index) => (
            <div
              key={column.key}
              className={`text-sm font-medium text-gray-700 ${column.width ? `flex-shrink-0 ${column.width}` : "flex-1 min-w-0"} ${column.className || ""}`}
              style={column.style}
            >
              <div className="flex items-center gap-1">
                {column.header}
                {column.sortable && <ChevronDown className="w-3 h-3 text-gray-400" />}
              </div>
            </div>
          ))}

          {showActions && (
            <div className="w-[80px] min-w-[80px] flex-shrink-0 text-center">
              <Trash2 className="w-4 h-4 text-gray-400 mx-auto" />
            </div>
          )}
        </div>
      )}

      {/* Mobile Select All */}
      {showSelectAll && (
        <div className="md:hidden bg-white rounded-xl px-4 py-3 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={e => handleSelectAll(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 focus:ring-2"
                style={{
                  accentColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                  "--tw-ring-color": `${Constants.HOST_EXPERIENCE_BASE_COLOR}50`,
                }}
              />
              <span className="text-sm font-medium text-gray-700">Select All ({data.length} items)</span>
            </div>
            <span className="text-xs text-gray-500">{selectedItems.length} selected</span>
          </div>
        </div>
      )}

      {data.map((item, index) => (
        <div key={item.id || index}>
          {/* Desktop Row */}
          <div
            className="hidden md:flex bg-white rounded-xl px-4 py-3 items-center w-full cursor-pointer border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all"
            onClick={() => {
              if (onRowClick) onRowClick(item);
            }}
          >
            {/* Checkbox + ID/First Column */}
            <div className="flex items-center gap-3 w-[120px] min-w-[120px] flex-shrink-0">
              <input
                type="checkbox"
                checked={isSelected(item)}
                onChange={e => {
                  e.stopPropagation();
                  handleSelectItem(item, e.target.checked);
                }}
                className="w-4 h-4 rounded border-gray-300 focus:ring-2"
                style={{
                  accentColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                  "--tw-ring-color": `${Constants.HOST_EXPERIENCE_BASE_COLOR}50`,
                }}
              />
              <span className="text-sm font-medium text-gray-700">
                {typeof columns[0]?.render === "function"
                  ? columns[0].render(item)
                  : item[columns[0]?.key] || `#${item.id}`}
              </span>
            </div>

            {/* Dynamic Columns */}
            {columns.slice(1).map((column, colIndex) => (
              <div
                key={column.key}
                className={`text-sm ${column.width ? `flex-shrink-0 ${column.width}` : "flex-1 min-w-0 overflow-hidden"} ${column.className || ""}`}
                style={column.style}
              >
                {typeof column.render === "function" ? column.render(item) : item[column.key]}
              </div>
            ))}

            {/* Actions */}
            {showActions && (
              <div className="flex items-center justify-center w-[80px] min-w-[80px] flex-shrink-0">
                <button
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  onClick={e => {
                    e.stopPropagation();
                    if (onDelete) onDelete(item);
                  }}
                >
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            )}
          </div>

          <div className="md:hidden flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow transition">
            {/* Checkbox */}
            <input
              type="checkbox"
              checked={isSelected(item)}
              onChange={e => handleSelectItem(item, e.target.checked)}
              className="mt-4 h-4 w-4 shrink-0 rounded border-gray-300 accent-orange-500"
            />

            {/* Thumbnail */}
            <img src={item.image} alt={item.name} className="h-12 w-12 rounded-lg object-cover mt-2" />

            {/* Info + Actions */}
            <div className="flex flex-1 flex-col gap-1">
              {/* Top row: Name + Status */}
              <div className="flex justify-between items-start">
                <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                <span
                  className={clsx(
                    "inline-flex items-center rounded-full px-3 py-0.5 text-xs font-medium",
                    item.status === "Booked" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-600"
                  )}
                >
                  {item.status}
                </span>
              </div>

              {/* Location */}
              <div className="flex items-center text-gray-500 text-xs gap-1">
                <MapPin className="h-3.5 w-3.5" />
                <span>
                  {typeof item.location === "object"
                    ? item.location?.address || "No address"
                    : item.location || "No location"}
                </span>
              </div>

              {/* Date + Delete */}
              <div className="flex items-center justify-between text-gray-500 text-xs mt-0.5">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{item.date}</span>
                </div>
                {showActions && (
                  <button
                    className="p-1 hover:text-red-500 transition"
                    onClick={e => {
                      e.stopPropagation();
                      if (onDelete) onDelete(item);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Empty State */}
      {data.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm px-4 py-8 text-center border border-gray-100">
          <p className="text-gray-500">No data available</p>
        </div>
      )}
    </div>
  );
};

export default HTable;
